import React, { useMemo } from 'react';

import { Footer, Header } from '$partials/global';
import { Carousel, CarouselWrapper, CarouselPagination } from '$partials/base/carousel';
import { SwiperSlide } from 'swiper/react';
import { SimpleImage } from '$partials/media';
import { ModuleDispatcher } from '$partials/modules';
import CuratedArticleCarousel from '$partials/modules/blocks/curated-article-carousel';
import { Button } from '$partials/ui';
import MediaGallery, { MediaGalleryContext, MediaGalleryProvider } from '$partials/global/media-gallery';
import { ShareButtonModal, ShareButtons } from '$partials/ui/share-button.jsx';
import { ArticleSchema } from '$partials/articles';
import { CtaBannerBlock } from '$partials/modules/blocks';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').ArticleDetailPage}
 */
const ArticleDetailPage = (props) => {

    const page = props.data.page;
    const mediaItems = useMemo(() => {
        return page.modules
            ?.filter(item => item.__typename === 'ArticleTextImageBlock')
            ?.flatMap(item => item.images || [])
            ?.filter(img => img?.image?.image)
            ?.map((img, index) => ({
                id: img.image.image.id || `article-image-${index}`,
                media: {
                    image: img.image.image
                }
            })) || [];
    }, [page?.modules]);

    return (
        <>
            <ArticleSchema
                page={page}
                url={props?.location} />

            <Header {...props.data.header} />

            <main id='main-content'>
                <section className='theme-light-alt bg-main-background'>
                    <div className='pt-108 pb-16 sm:pt-124 md:pt-160 sm:pb-48'>
                        <div className='layout-container layout-site-outer-padding'>
                            <div className='col-wrapper flex flex-col gap-y-16 sm:gap-y-24 md:flex-row'>
                                <div className='col-padding md:w-8/12'>
                                    {page?.topics?.length > 0 && (
                                        <div className='flex flex-wrap gap-6 w-full mb-24 md:mb-16'>
                                            {page.topics.map((topic, index) => (
                                                <Button
                                                    as='a'
                                                    className='bg-tag-main-background border border-tag-main-border rounded flex h-24 items-center justify-center px-8 py-8'
                                                    href={`/topics/${topic.slug}`}
                                                    key={index}>
                                                    <p className='type-style-eyebrow text-tag-main-text'>
                                                        {topic.title}
                                                    </p>
                                                </Button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className='layout-container layout-site-outer-padding'>
                            <div className='col-wrapper flex flex-col gap-y-16 sm:gap-y-24 md:flex-row'>
                                <div className='col-padding md:w-8/12'>
                                    <h1 className='text-main-text-primary type-style-1'>
                                        {page?.title}
                                    </h1>
                                </div>
                                <div className='flex flex-col gap-y-4 text-main-text-secondary col-padding md:w-4/12'>
                                    {page?.authorName && (
                                        <p className='type-style-5'>{page.authorName}</p>
                                    )}
                                    {page?.authorRole && (
                                        <p className='type-style-eyebrow'>{page.authorRole}</p>
                                    )}
                                    {page?.authorOffice && (
                                        <p className='type-style-eyebrow'>{page.authorOffice}</p>
                                    )}
                                    {/* Share Button */}
                                    <div className='col-wrapper mt-16'>
                                        <div className='col-padding md:w-8/12'>
                                            <ShareButtonModal
                                                author={page?.authorName}
                                                height='h-32'
                                                title={page?.title || ''}
                                                url={props?.location}
                                                width='w-32'/>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {page?.images?.length > 0 && (
                        <Carousel
                            swiperConfig={{
                                a11y: { enabled: true },
                                slidesPerView: 1,
                                grabCursor: false,
                            }}>
                            <div className='relative'>
                                <CarouselWrapper>
                                    {page?.images?.map((item) => {
                                        return (
                                            <SwiperSlide
                                                aria-roledescription='slide'
                                                className='select-none cursor-pointer'
                                                key={item.id}
                                                role='group'>
                                                <SimpleImage
                                                    className='w-full h-[195px] sm:h-[395px] md:h-[740px] object-cover'
                                                    {...item?.image?.image}/>

                                            </SwiperSlide>
                                        );
                                    })}
                                </CarouselWrapper>
                                {page?.images?.length > 1 && (
                                    <CarouselPagination totalSlides={page.images.length} />
                                )}
                            </div>
                        </Carousel>
                    )}
                </section>

                {page?.standfirst && (
                    <section className='theme-light bg-main-background'>
                        <div className='layout-container layout-site-outer-padding py-24 sm:py-40 md:py-48'>
                            <h2 className='type-style-2 text-main-text-primary w-full md:w-9/12'>
                                {page.standfirst}
                            </h2>
                        </div>
                    </section>
                )}
                <MediaGalleryProvider>
                    {page?.modules?.length > 0 && (
                        <section className='theme-light-alt'>
                            {page.modules.map((module, i) => (
                                <ModuleDispatcher
                                    {...module}
                                    key={i + module?.id}/>
                            ))}
                        </section>
                    )}

                    <ShareButtons
                        title={page?.title || ''}
                        url={props?.location}/>

                    <MediaGalleryContext.Consumer>
                        {({ galleryInitialId }) => {
                            return (
                                galleryInitialId && mediaItems?.length > 0 && (
                                    <MediaGallery
                                        initialId={galleryInitialId}
                                        items={mediaItems} />
                                )
                            );
                        }}
                    </MediaGalleryContext.Consumer>
                </MediaGalleryProvider>
                <section className='theme-light bg-main-background'>
                    {page?.similarArticles?.edges?.length > 0 && (
                        <CuratedArticleCarousel
                            heading='Related Articles'
                            items={page.similarArticles.edges.map((edge) => ({
                                article: edge.node
                            }))} />
                    )}
                </section>
            </main>

            <CtaBannerBlock
                body={props.data.footer?.getInTouchBodyText}
                heading={props.data.footer?.getInTouchHeadingText}
                opensDialog={true} />
            <Footer {...props.data.footer} />
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default ArticleDetailPage;
